import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Flame,
  Layers,
  Loader2,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import {
  buildOperatorAttentionFlags,
  formatKickoffUnix,
  formatOddsValue,
  formatOutcomeLabel,
  riskDeskIsClear,
} from "../../utils/riskDeskHelpers";

const PREVIEW_OUTCOMES = 4;
const PREVIEW_TICKETS = 3;

const AttentionBanner = ({ tone = "info", title, body }) => (
  <div className={`oc-alert oc-risk-alert oc-risk-alert-${tone}`}>
    <AlertTriangle size={14} />
    <div>
      <strong>{title}</strong>
      <p>{body}</p>
    </div>
  </div>
);

const TicketHighlightRow = ({ ticket, rank, mode }) => {
  const isLiability = mode === "liability";
  return (
    <li className="oc-risk-ticket-row">
      <span className="oc-risk-ticket-rank">{rank + 1}</span>
      <div className="oc-risk-ticket-copy">
        <strong>{ticket.username || "Bettor"}</strong>
        <span>
          {isLiability
            ? `Stake ${formatCash(ticket.stake || 0, { compact: true })} · ${ticket.legCount ?? 0} leg${
                (ticket.legCount || 0) === 1 ? "" : "s"
              }`
            : `Liability ${formatCash(ticket.liability || 0, { compact: true })} · stake ${formatCash(
                ticket.stake || 0,
                { compact: true }
              )}`}
          {typeof ticket.totalOddsValue === "number"
            ? ` · ${Number(ticket.totalOddsValue).toFixed(2)} odds`
            : ""}
        </span>
      </div>
      <div className="oc-risk-ticket-side">
        <em className="tabular-nums">
          {isLiability
            ? formatCash(ticket.liability || 0, { compact: true })
            : `${ticket.legCount ?? 0} games`}
        </em>
        <span>{isLiability ? "Max pay" : "Legs"}</span>
      </div>
    </li>
  );
};

const FixtureExposureCard = ({ fixture, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const outcomes = fixture.outcomes || [];
  const visibleOutcomes = expanded ? outcomes : outcomes.slice(0, PREVIEW_OUTCOMES);

  return (
    <article className="oc-risk-fixture-card">
      <div className="oc-risk-fixture-top">
        <div className="oc-risk-fixture-copy">
          <strong>{fixture.matchLabel || "Match"}</strong>
          <span>
            {fixture.statusShort ? `${fixture.statusShort} · ` : ""}
            {formatKickoffUnix(fixture.kickoffUnix)}
          </span>
        </div>
        <div className="oc-risk-fixture-kpi">
          <em className="tabular-nums">{formatCash(fixture.openStakeTotal || 0, { compact: true })}</em>
          <span>Cash at stake</span>
        </div>
      </div>

      <div className="oc-risk-fixture-metrics">
        <span>{fixture.openTicketCount || 0} open tickets</span>
        <span>{outcomes.length} outcomes touched</span>
      </div>

      {outcomes.length > 0 ? (
        <>
          <p className="oc-risk-outcome-label">Outcome pressure</p>
          <ul className="oc-risk-outcomes">
            {visibleOutcomes.map((outcome, idx) => (
              <li key={`${fixture.fixtureId}-${idx}`}>
                <div className="oc-risk-outcome-copy">
                  <strong>{formatOutcomeLabel(outcome)}</strong>
                  <span>Odds {formatOddsValue(outcome.oddValue)}</span>
                </div>
                <div className="oc-risk-outcome-side">
                  <em className="tabular-nums">
                    {formatCash(outcome.stakeSum || 0, { compact: true })}
                  </em>
                  <span>
                    {outcome.ticketCount || 0} ticket
                    {(outcome.ticketCount || 0) === 1 ? "" : "s"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          {outcomes.length > PREVIEW_OUTCOMES ? (
            <button
              type="button"
              className="oc-risk-expand-btn"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? (
                <>
                  <ChevronUp size={14} />
                  Show fewer outcomes
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  Show {outcomes.length - PREVIEW_OUTCOMES} more outcomes
                </>
              )}
            </button>
          ) : null}
        </>
      ) : null}
    </article>
  );
};

const OperatorRiskPanel = ({ bet, dashboard, houseId, loading, onRefresh }) => {
  const [showAllFixtures, setShowAllFixtures] = useState(false);
  const [expandLiability, setExpandLiability] = useState(false);
  const [expandParlays, setExpandParlays] = useState(false);

  const fixtures = dashboard?.fixtures || {};
  const treasury = dashboard?.treasury || {};
  const activity = dashboard?.activity || {};
  const operatorHealth = dashboard?.operatorHealth || {};
  const openFixtureBoard = dashboard?.openFixtureOutcomeBoard || [];
  const ticketHighlights = dashboard?.openTicketsHighlights || {};
  const topByLiability = ticketHighlights.topByLiability || [];
  const topByLegCount = ticketHighlights.topByLegCount || [];

  const closingSoon = Number(fixtures.closingSoon || 0);
  const suspendedFixtures = Number(fixtures.suspended || 0);
  const fixturesNeedingOdds = Number(operatorHealth.fixturesNeedingOdds || 0);
  const bettableUpcoming = Number(operatorHealth.bettableUpcomingFixtures || 0);
  const minSelectionsRequired = Number(operatorHealth.minSelectionsRequired || 2);

  const { showAlerts, needsAttention } = useMemo(
    () =>
      buildOperatorAttentionFlags({
        operatorHealth,
        closingSoon,
        suspendedFixtures,
      }),
    [operatorHealth, closingSoon, suspendedFixtures]
  );

  const allClear = useMemo(
    () =>
      riskDeskIsClear({
        showAlerts,
        openFixtureBoard,
        topByLiability,
        topByLegCount,
      }),
    [showAlerts, openFixtureBoard, topByLiability, topByLegCount]
  );

  const fixturePreview = openFixtureBoard[0] || null;
  const extraFixtures = openFixtureBoard.slice(1);
  const visibleExtraFixtures = showAllFixtures ? extraFixtures : [];
  const liabilityList = expandLiability ? topByLiability : topByLiability.slice(0, PREVIEW_TICKETS);
  const parlayList = expandParlays ? topByLegCount : topByLegCount.slice(0, PREVIEW_TICKETS);
  const ticketsPath = `/app/bethouse/manage/${houseId}/tickets`;

  if (!dashboard && loading) {
    return (
      <section className="oc-panel oc-risk-panel">
        <header className="oc-panel-head oc-partners-head">
          <div>
            <p className="oc-loan-eyebrow">Risk posture</p>
            <h2>House exposure</h2>
            <p>Fixture pressure, open-ticket liability, and operator health indicators.</p>
          </div>
        </header>
        <div className="oc-loan-loading">
          <Loader2 size={22} className="animate-spin" />
          <span>Loading risk posture…</span>
        </div>
      </section>
    );
  }

  return (
    <section className="oc-panel oc-risk-panel">
      <header className="oc-panel-head oc-partners-head">
        <div>
          <p className="oc-loan-eyebrow">Risk posture</p>
          <h2>House exposure</h2>
          <p>Fixture pressure, open-ticket liability, and operator health indicators.</p>
        </div>
        <div className="oc-partners-head-meta">
          <span
            className={`oc-partners-status-pill ${
              needsAttention ? "oc-partners-status-locked" : "oc-partners-status-ready"
            }`}
          >
            {needsAttention ? "ATTENTION" : "CLEAR"}
          </span>
          <button
            type="button"
            className="oc-loan-icon-btn"
            onClick={onRefresh}
            disabled={loading}
            aria-label="Refresh risk data"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      <div className="oc-stat-grid oc-stat-grid-compact">
        <div className="oc-stat">
          <em>Locked liability</em>
          <strong className="tabular-nums">
            {formatCash(treasury.lockedLiability ?? 0, { compact: true })}
          </strong>
          <span>Unsettled exposure</span>
        </div>
        <div className="oc-stat">
          <em>Open tickets</em>
          <strong>{activity.openTickets ?? "—"}</strong>
          <span>Active positions</span>
        </div>
        <div className="oc-stat">
          <em>Pending book</em>
          <strong className="tabular-nums">
            {formatCash(treasury.pendingBalanceBook ?? bet?.pendingBalance ?? 0, {
              compact: true,
            })}
          </strong>
          <span>In-flight stakes</span>
        </div>
        <div className="oc-stat oc-stat-accent">
          <em>Available</em>
          <strong className="tabular-nums">
            {formatCash(
              treasury.amountAvailableBook ??
                treasury.availableBalance ??
                bet?.amountAvailable ??
                0,
              { compact: true }
            )}
          </strong>
          <span>Treasury headroom</span>
        </div>
      </div>

      <div className="oc-stat-grid oc-stat-grid-compact">
        <div className={`oc-stat ${closingSoon > 0 ? "oc-stat-accent" : ""}`}>
          <em>Closing soon</em>
          <strong>{closingSoon}</strong>
          <span>Next 24 hours</span>
        </div>
        <div className={`oc-stat ${suspendedFixtures > 0 ? "oc-stat-accent" : ""}`}>
          <em>Suspended</em>
          <strong>{suspendedFixtures}</strong>
          <span>No new legs</span>
        </div>
        <div className={`oc-stat ${fixturesNeedingOdds > 0 ? "oc-stat-need-odds" : ""}`}>
          <em>Need odds</em>
          <strong>{fixturesNeedingOdds}</strong>
          <span>Missing prices</span>
        </div>
        <div className="oc-stat">
          <em>Upcoming</em>
          <strong>{bettableUpcoming}</strong>
          <span>NS with odds</span>
        </div>
      </div>

      {showAlerts ? (
        <div className="oc-risk-attention-stack">
          <header className="oc-risk-section-head">
            <Flame size={14} />
            <div>
              <h3>What needs attention</h3>
              <p>Operator alerts from fixture pool and house health checks.</p>
            </div>
          </header>

          {operatorHealth.autoPauseImminent ? (
            <AttentionBanner
              tone="critical"
              title="Auto-pause risk"
              body={`Bettors need at least ${minSelectionsRequired} upcoming match${
                minSelectionsRequired === 1 ? "" : "es"
              } with odds. You currently have ${bettableUpcoming}. Add fixtures or adjust min legs.`}
            />
          ) : null}
          {operatorHealth.atMinimumBettablePool && !operatorHealth.autoPauseImminent ? (
            <AttentionBanner
              tone="warn"
              title="At minimum bettable count"
              body={`You have exactly ${minSelectionsRequired} fixture${
                minSelectionsRequired === 1 ? "" : "s"
              } keeping the house open.`}
            />
          ) : null}
          {operatorHealth.lowBettableHeadroom &&
          !operatorHealth.autoPauseImminent &&
          !operatorHealth.atMinimumBettablePool ? (
            <AttentionBanner
              tone="warn"
              title="Thin fixture cushion"
              body={`Only ${bettableUpcoming} upcoming NS fixture${
                bettableUpcoming === 1 ? "" : "s"
              } with odds.`}
            />
          ) : null}
          {fixturesNeedingOdds > 0 ? (
            <AttentionBanner
              tone="need-odds"
              title={`${fixturesNeedingOdds} fixture${fixturesNeedingOdds === 1 ? "" : "s"} need odds`}
              body="Bettors only see games with published odds and a future kickoff."
            />
          ) : null}
          {closingSoon > 0 ? (
            <AttentionBanner
              tone="info"
              title="Kickoffs within 24 hours"
              body={`${closingSoon} linked fixture${closingSoon === 1 ? "" : "s"} kick off in the next 24 hours.`}
            />
          ) : null}
          {suspendedFixtures > 0 ? (
            <AttentionBanner
              tone="info"
              title={`${suspendedFixtures} suspended fixture${suspendedFixtures === 1 ? "" : "s"}`}
              body="Bettors cannot add new legs on suspended games."
            />
          ) : null}
        </div>
      ) : null}

      {openFixtureBoard.length > 0 ? (
        <div className="oc-risk-block">
          <header className="oc-risk-section-head">
            <Flame size={14} />
            <div>
              <h3>Fixture risk map</h3>
              <p>Where open tickets are concentrated by match and outcome.</p>
            </div>
            <span className="oc-risk-pill">{openFixtureBoard.length} games</span>
          </header>

          {fixturePreview ? (
            <FixtureExposureCard fixture={fixturePreview} defaultExpanded />
          ) : null}

          {visibleExtraFixtures.map((fixture) => (
            <FixtureExposureCard key={fixture.fixtureId} fixture={fixture} />
          ))}

          {extraFixtures.length > 0 ? (
            <button
              type="button"
              className="oc-risk-load-more"
              onClick={() => setShowAllFixtures((v) => !v)}
            >
              {showAllFixtures ? (
                <>
                  <ChevronUp size={14} />
                  Show fewer games
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  View all {openFixtureBoard.length} games
                </>
              )}
            </button>
          ) : null}
        </div>
      ) : null}

      {topByLiability.length > 0 || topByLegCount.length > 0 ? (
        <div className="oc-risk-block">
          <header className="oc-risk-section-head">
            <Layers size={14} />
            <div>
              <h3>Slip risk board</h3>
              <p>Tickets that can move liability or complexity.</p>
            </div>
            <span className="oc-risk-pill">
              {Math.max(topByLiability.length, topByLegCount.length)} tracked
            </span>
          </header>

          {topByLiability.length > 0 ? (
            <div className="oc-risk-ticket-panel">
              <header className="oc-risk-ticket-panel-head">
                <TrendingUp size={13} />
                <span>Highest payout risk</span>
              </header>
              <ul className="oc-risk-ticket-list">
                {liabilityList.map((ticket, idx) => (
                  <TicketHighlightRow
                    key={String(ticket.ticketId)}
                    ticket={ticket}
                    rank={idx}
                    mode="liability"
                  />
                ))}
              </ul>
              {topByLiability.length > PREVIEW_TICKETS ? (
                <button
                  type="button"
                  className="oc-risk-expand-btn"
                  onClick={() => setExpandLiability((v) => !v)}
                >
                  {expandLiability
                    ? "Show fewer tickets"
                    : `Show ${topByLiability.length - PREVIEW_TICKETS} more tickets`}
                </button>
              ) : null}
            </div>
          ) : null}

          {topByLegCount.length > 0 ? (
            <div className="oc-risk-ticket-panel">
              <header className="oc-risk-ticket-panel-head">
                <Layers size={13} />
                <span>Deepest parlays</span>
              </header>
              <ul className="oc-risk-ticket-list">
                {parlayList.map((ticket, idx) => (
                  <TicketHighlightRow
                    key={String(ticket.ticketId)}
                    ticket={ticket}
                    rank={idx}
                    mode="legs"
                  />
                ))}
              </ul>
              {topByLegCount.length > PREVIEW_TICKETS ? (
                <button
                  type="button"
                  className="oc-risk-expand-btn"
                  onClick={() => setExpandParlays((v) => !v)}
                >
                  {expandParlays
                    ? "Show fewer tickets"
                    : `Show ${topByLegCount.length - PREVIEW_TICKETS} more tickets`}
                </button>
              ) : null}
            </div>
          ) : null}

          <Link to={ticketsPath} className="oc-risk-load-more oc-risk-load-more-link">
            View all open tickets
          </Link>
        </div>
      ) : null}

      {bet?.status === "active" && bet?.goLiveReadiness && !bet.goLiveReadiness.canOfferParlays ? (
        <div className="oc-alert">
          <AlertTriangle size={14} />
          <div>
            <strong>Market not ready for bettors</strong>
            <p>Fix fixtures, odds, treasury, or limits before offering parlays.</p>
          </div>
        </div>
      ) : null}

      {allClear ? (
        <div className="oc-risk-clear">
          <ShieldCheck size={28} strokeWidth={1.5} />
          <strong>No risk hotspots</strong>
          <p>Nothing flagged. Keep an eye on closing fixtures and incoming stakes.</p>
        </div>
      ) : null}
    </section>
  );
};

export default OperatorRiskPanel;
